import { useState } from 'react';

import { Select, Spin } from 'antd';
import { useGetAllRelationGroupsInfiniteInfiniteQuery, useLazyGetRelationGroupByIdQuery } from 'services';
import useDebounce from 'hooks/useDebounce';

import { mutedNoteStyle } from '../muted-text-style';
import PostOrderOnlyNotice from '../post-order-only-notice';

/**
 * Seçilemeyen grubun satırı. Eklenecek madde Telif Sonrası'nda değilse kimlik kartı ortak gruplar gizlenmez, pasif ve
 * notuyla görünür; kullanıcı aradığı grubu bulup nedenini görür.
 */
function SharedGroupOptionLabel({ name }) {
  return (
    <div className="gx-d-flex gx-align-items-center gx-justify-content-between" style={{ gap: 8 }}>
      <span className="gx-text-truncate">{name}</span>
      <span style={mutedNoteStyle}>Kimlik kartı ortak</span>
    </div>
  );
}

/**
 * Mevcut gruplar arasında arama yaparak bir grup seçmeyi sağlar.
 * Focus'ta ilk sayfa yüklenir, infinite scroll ile devam eder.
 * Seçim yapıldığında detail endpoint'inden full veri çekilir.
 * @param {Object} props
 * @param {Function} props.onGroupSelect - Grup seçildiğinde callback. ({ id, name, members })
 * @param {boolean} [props.postOrderOnly=false] - true ise kimlik kartı ortak gruplar seçilemez (eklenecek madde Telif Sonrası'nda değil)
 */
function SelectGroup({ onGroupSelect, postOrderOnly = false }) {
  const [searchText, setSearchText] = useState('');
  const { handleDebounce } = useDebounce();

  const { data, isFetching, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useGetAllRelationGroupsInfiniteInfiniteQuery({ name: searchText || undefined });

  const [getGroupById, { isFetching: isFetchingDetail }] = useLazyGetRelationGroupByIdQuery();

  const pages = data?.pages ?? [];
  const rawGroups = pages.flatMap((page) => page['hydra:member']);

  const allGroups = Array.from(
    new Map(rawGroups.map((group) => [group.id, group])).values(),
  );

  const handleSearch = (value) => {
    handleDebounce({
      callback: () => setSearchText(value),
    });
  };

  const handlePopupScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 20 && !isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  };

  const handleSelect = async (_, option) => {
    const { data: fullGroup } = await getGroupById({ groupId: option.value });
    if (!fullGroup) return;
    const members = (fullGroup.members ?? []).map((member) => ({
      id: member.articleOffer.id,
      '@id': member.articleOffer['@id'],
      label: member.articleOffer.title,
      memberId: member['@id'],
    }));

    onGroupSelect({ id: fullGroup.id, name: fullGroup.name, members });
  };

  return (
    <Spin spinning={isFetchingDetail}>
      <div>
        <label>
          Madde Grubu Ara{' '}
          <span style={{ color: '#f5222d' }}>*</span>
        </label>
        <Select
          className="gx-mt-2 gx-w-100"
          placeholder="Grup adı ile arayın..."
          options={allGroups.map((group) => {
            // BE engeli gibi yalnız `shareIdentification`'a bakılır; liste rozetindeki Ortak kuralına değil.
            const disabled = postOrderOnly && group.shareIdentification === true;
            return {
              label: disabled ? <SharedGroupOptionLabel name={group.name} /> : group.name,
              value: group.id,
              disabled,
            };
          })}
          dropdownRender={(menu) => (
            <>
              {postOrderOnly && <PostOrderOnlyNotice />}
              {menu}
            </>
          )}
          loading={isFetching}
          onSearch={handleSearch}
          onPopupScroll={handlePopupScroll}
          showSearch
          filterOption={false}
          onSelect={handleSelect}
          disabled={isFetchingDetail}
        />
      </div>
    </Spin>
  );
}

export default SelectGroup;
